/* eslint-disable simple-import-sort/imports */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ProjectContext } from '../../../../../state/project-state/project-state';
import { Button } from '../../../../button/button';
import { Dialog } from '../../../../dialog/dialog';
import { MultiSelectDropdown } from '../../../../multi-select-dropdown/multi-select-dropdown';
import { AssetTagChip } from '../../components/asset-tag-chip';
import { Stack } from '../../../../stack/stack';
import { Icon } from '../../../../icon/icon';
import styled from 'styled-components';

type TagGroupWithId = { id: string; tagIds: string[] };

type Suggestion = { id: string; name: string };

type Props = {
  onClose: (tagGroups?: string[][]) => void;
  tagGroups?: string[][];
};

const TagFilterDialog = ({ onClose, tagGroups }: Props) => {
  const { project } = React.useContext(ProjectContext);
  const { t } = useTranslation();

  const [selectedTagGroups, setSelectedTagGroups] = React.useState<TagGroupWithId[]>(
    tagGroups?.map((group) => ({ id: crypto.randomUUID(), tagIds: group })) ?? [
      createEmptyTagGroupWithId(),
    ],
  );

  const suggestions: Suggestion[] = (project?.assetTags ?? []).map((t) => ({
    id: t.id,
    name: t.name,
  }));

  const filterPredicate = React.useCallback((input: string, s: Suggestion) => {
    return s.name.toLowerCase().includes(input.toLowerCase());
  }, []);

  return (
    <Dialog closeIcon={true} closeOnDimmerClick={true} width="350px" onClose={() => onClose()}>
      {{
        header: t('topArea.filter.tags', { ns: 'skyviewAssetMenu' }),
        content: (
          <Stack spacing={2}>
            <Stack spacing={0.5}>
              {selectedTagGroups?.map((selectedGroup, selectedGroupIndex, arr) => (
                <React.Fragment key={selectedGroup.id}>
                  {selectedGroupIndex > 0 && (
                    <OrLabel>{t('tagDialog.orLabel', { ns: 'skyviewAssetMenu' })}</OrLabel>
                  )}
                  <Stack alignItems="center" direction="row" spacing={0.5}>
                    <MultiSelectDropdown
                      allowCreateNew={false}
                      filterPredicate={filterPredicate}
                      getChipText={(s) => s.name}
                      hideClearAllButton={true}
                      id={selectedGroup.id}
                      options={suggestions}
                      placeholder={t('tagDialog.placeholder', { ns: 'skyviewAssetMenu' })}
                      renderOption={(opt) => <AssetTagChip tag={{ id: opt.id }} />}
                      renderSelectedChip={(opt, onRemove) => (
                        <AssetTagChip tag={{ id: opt.id }} onClose={onRemove} />
                      )}
                      selectedIds={selectedGroup.tagIds}
                      suggestionsLimit={5}
                      onChange={(ids) =>
                        setSelectedTagGroups((prev) =>
                          prev.map((group) =>
                            group.id === selectedGroup.id ? { ...group, tagIds: ids } : group,
                          ),
                        )
                      }
                    />
                    {arr.length > 1 && (
                      <Icon
                        icon={['fal', 'trash-can']}
                        size="lg"
                        onClick={() => {
                          setSelectedTagGroups((prev) =>
                            prev.filter((group) => group.id !== selectedGroup.id),
                          );
                        }}
                        onHoverStyle={{ icon: ['fas', 'trash-can'] }}
                      />
                    )}
                  </Stack>
                </React.Fragment>
              ))}
            </Stack>
            <Button
              variant="contained"
              onClick={() => {
                setSelectedTagGroups((prev) => [...prev, createEmptyTagGroupWithId()]);
              }}
            >
              {t('tagDialog.addGroupButton', { ns: 'skyviewAssetMenu' })}
            </Button>
          </Stack>
        ),
        footer: {
          right: (
            <>
              <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  const nonEmptySelectedTagGroups = selectedTagGroups
                    .filter((filter) => filter.tagIds.length > 0)
                    .map((group) => group.tagIds);

                  onClose(
                    nonEmptySelectedTagGroups.length > 0 ? nonEmptySelectedTagGroups : undefined,
                  );
                }}
              >
                {t('confirm', { ns: 'common' })}
              </Button>
              <Button variant="text" onClick={() => onClose()}>
                {t('cancel', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

const createEmptyTagGroupWithId = (): TagGroupWithId => ({
  id: crypto.randomUUID(),
  tagIds: [],
});

const OrLabel = styled.span`
  align-self: center;
`;

export { TagFilterDialog };
